import React, { useContext } from 'react'

import { Context } from 'types/GlobalUserEvents'
import { FlattenedNode } from 'components_new/molecules/TreeDndTable/types'

import { useAccount, useAccounts } from 'redux/hooks/Accounts'
import { useIsMobile } from 'utils/hooks'
import { useCurrencyConverter } from 'redux/hooks/CurrencyConverter'
import { useKeyMetric, useKeyMetricRawData } from 'redux/hooks/KeyMetrics'
import { useMetric } from 'redux/hooks/Metrics'
import { useTranslation } from 'language/translator'

import { KeyMetricsTreeContext } from './index'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import Skeleton from 'components_new/atoms/Skeleton'
import TableCell from 'components_new/atoms/Table/TableCell'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import ChangeText from 'components_new/molecules/ChangeText'
import GroupsChip from 'components_new/molecules/GroupsChip'
import LineChart from 'components_new/molecules/charts/LineChart'
import NameCell, {
  useTreeDndTable
} from 'components_new/molecules/TreeDndTable/NameCell'
import SearchAndSelectPopover from 'components_new/molecules/SearchAndSelectPopover'
import UserAvatar from 'components_new/molecules/UserAvatar'
import ValueText from 'components_new/molecules/ValueText'

import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import ResourcesChip from 'components_new/molecules/ResourcesChip'

import { getLabels, getLineColor, getValues, resolveUnit } from 'utils/metric'

import { addRef } from 'utils/queryHandler'
import { formatUsername } from 'utils/functions'
import { AUTO_SIGNIFICANT_FIGURES } from 'utils/autoFormat'
import { getResourceVariant } from 'types/GlobalKeyMetric'

const KeyMetricCells = ({ node }: { node: FlattenedNode }) => {
  const {
    groupId,
    onAdd,
    onDelete,
    onEdit,
    onGroups,
    onInfo,
    onOwner,
    onPin,
    onPreview,
    onResources
  } = useContext(KeyMetricsTreeContext)

  const isMobile = useIsMobile()

  const { accounts } = useAccounts()
  const keyMetric = useKeyMetric(node.data.id)
  const settings = keyMetric?.metric.settings

  const responsibleAccount = useAccount(
    keyMetric?.responsible_account_id ?? null
  )

  const metric = useMetric(
    keyMetric?.metric.id,
    settings?.variant_kpi_template_id
  )
  const { t } = useTranslation()
  const { editMode } = useTreeDndTable()
  const currency = useCurrencyConverter()

  const [rawData, rawDataError] = useKeyMetricRawData(keyMetric?.id ?? '')
  const { lang } = useTranslation()

  const dataLoading = !rawData && rawDataError === null

  // loading
  if (!keyMetric || (!metric && rawDataError !== 404) || dataLoading) {
    if (isMobile)
      return (
        <>
          {[1, 2].map((i) => (
            <TableCell key={i}>
              <Skeleton sx={{ minWidth: 96 }} variant={'text'} width="100%" />
            </TableCell>
          ))}
        </>
      )

    return (
      <>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <TableCell key={i}>
            <Skeleton
              height={i === 6 ? 24 : i === 7 ? 28 : undefined}
              variant={i === 7 ? 'rounded' : i === 10 ? 'circular' : 'text'}
              width={
                i === 4 || i === 8 || i === 9 ? 24 : i === 10 ? 14 : undefined
              }
            />
          </TableCell>
        ))}
      </>
    )
  }

  // controlled values
  const labels = getLabels({ lang, rawData, segmentOption: null })
  const hasError = rawDataError !== null
  const increaseIsPositive = keyMetric.increase_is_positive
  const values = getValues({ rawData }, hasError)

  // values
  const rawUnit = metric?.unit ?? t('global', 'pcs')
  const unit = resolveUnit(rawUnit, currency) ?? rawUnit
  const value = values.filter((v) => v !== null).pop() ?? null
  const target =
    keyMetric.target_activated && rawData?.target !== null
      ? rawData?.target.filter((d) => d.value !== null).pop()?.value ?? null
      : null

  const errorMessage =
    rawDataError === 404 && !metric
      ? t('key_metrics', 'metric_unavailable')
      : hasError
        ? t('global', 'error_500_title')
        : undefined

  const TitleComponent = (
    <Text component="div" noWrap={true} variant="navigation">
      <Link
        href={addRef(
          `/key-metrics/${keyMetric.id}`,
          Context.FROM_KEY_METRICS_PAGE
        )}
      >
        {keyMetric.title}
      </Link>{' '}
      {keyMetric.pinned ? (
        <Icon
          color="disabled"
          fontSize="inherit"
          name="Pin"
          sx={{ verticalAlign: 'middle' }}
        />
      ) : null}
      <Tooltip title={t('global', 'more_information')}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            onInfo(keyMetric)
          }}
          size="small"
          sx={{ opacity: 0, 'tr:hover &': { opacity: 1 } }}
        >
          <Icon color="disabled" fontSize="inherit" name="Info" />
        </IconButton>
      </Tooltip>
    </Text>
  )

  const ValueComponent = (
    <ValueText
      align={isMobile ? 'left' : 'right'}
      significantFigures={AUTO_SIGNIFICANT_FIGURES}
      error={errorMessage}
      unit={isMobile ? unit : undefined}
      value={value}
    />
  )

  const FullChangeComponent = (
    <ChangeText
      align="right"
      denominator={values[0]}
      error={errorMessage}
      increaseIsPositive={increaseIsPositive}
      label={isMobile ? '12m' : undefined}
      numerator={values[values.length - 1]}
      unit={metric?.unit}
    />
  )

  const LatestChangeComponent = (
    <ChangeText
      align="right"
      denominator={values[values.length - 2]}
      error={errorMessage}
      increaseIsPositive={increaseIsPositive}
      label={isMobile ? '1m' : undefined}
      numerator={values[values.length - 1]}
      unit={metric?.unit}
    />
  )

  if (isMobile)
    return (
      <>
        {/*-- title --*/}
        <NameCell>
          <Box>
            <Box sx={{ mb: 0.5 }}>{TitleComponent}</Box>
            {ValueComponent}
          </Box>
        </NameCell>

        {/*-- content --*/}
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{FullChangeComponent}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>{LatestChangeComponent}</Box>
          </Box>
        </TableCell>
      </>
    )

  return (
    <>
      {/*-- title --*/}
      <NameCell>{TitleComponent}</NameCell>

      {/*-- value --*/}
      <TableCell>{ValueComponent}</TableCell>

      {/*-- target --*/}
      <TableCell>
        <ValueText
          align="right"
          error={errorMessage}
          significantFigures={AUTO_SIGNIFICANT_FIGURES}
          value={target}
        />
      </TableCell>

      {/*-- unit --*/}
      <TableCell>
        <Text
          align="left"
          color="text.secondary"
          component="div"
          noWrap={true}
          variant="navigation"
        >
          {unit}
        </Text>
      </TableCell>

      {/*-- 12m change --*/}
      <TableCell>{FullChangeComponent}</TableCell>

      {/*-- 1m change --*/}
      <TableCell>{LatestChangeComponent}</TableCell>

      {/*-- trend --*/}
      <TableCell
        onClick={(e) => {
          e.stopPropagation()
          if (hasError) return
          onPreview(keyMetric)
        }}
        sx={{
          ['&:hover']: hasError
            ? {}
            : { bgcolor: 'action.hover', cursor: 'zoom-in' }
        }}
      >
        <Box
          sx={{
            height: 24
          }}
        >
          <LineChart
            data={{
              labels: labels,
              isTimeseries: true,
              datasets: [
                {
                  color: getLineColor({
                    increaseIsPositive: increaseIsPositive,
                    values: values
                  }),
                  label: '',
                  type: 'line',
                  unit: metric?.unit ?? null,
                  values: values
                }
              ]
            }}
            variant="minimal"
          />
        </Box>
      </TableCell>

      {/*-- resources --*/}
      <TableCell padding="none">
        <ResourcesChip
          onClick={() => onResources(keyMetric)}
          resources={keyMetric.resources.map((r) => ({
            id: r.id,
            title: r.title,
            variant: getResourceVariant(r)
          }))}
        />
      </TableCell>

      {/*-- groups --*/}
      <TableCell padding="none">
        <GroupsChip
          groups={keyMetric.groups}
          onClick={() => onGroups(keyMetric)}
        />
      </TableCell>

      {/*-- owner --*/}
      <TableCell padding="none">
        <SearchAndSelectPopover
          multiple={false}
          onChange={(value) => onOwner(keyMetric, value)}
          options={accounts.map((account) => {
            return {
              avatar: 'user',
              body: account.email,
              id: account.id,
              title: formatUsername(account)
            }
          })}
          tooltip={
            responsibleAccount
              ? `${t('global', 'owner')}: ${formatUsername(responsibleAccount)}`
              : t('global', 'no_owner')
          }
          value={keyMetric.responsible_account_id}
          sx={{ mr: -1 }}
        >
          <IconButton size="small">
            <UserAvatar
              email={responsibleAccount ? responsibleAccount.email : undefined}
              name={
                responsibleAccount
                  ? formatUsername(responsibleAccount)
                  : undefined
              }
              size="small"
            />
          </IconButton>
        </SearchAndSelectPopover>
      </TableCell>

      {/*-- actions --*/}
      {editMode ? (
        <TableCell>
          <Box sx={{ alignItems: 'center', display: 'flex' }}>
            {groupId ? (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  onPin(keyMetric) // $TODO: include groupId
                }}
                size="small"
              >
                <Icon
                  color="disabled"
                  name={keyMetric.pinned ? 'Unpin' : 'Pin'}
                />
              </IconButton>
            ) : (
              <OptionsMenuButton
                options={[
                  {
                    divider: true,
                    iconName: 'AddRow' as const,
                    onClick: () => onAdd(keyMetric),
                    title: t('key_metrics', 'add_operational_title')
                  },
                  {
                    divider: true,
                    iconName: keyMetric.pinned
                      ? ('Unpin' as const)
                      : ('Pin' as const),
                    onClick: () => onPin(keyMetric),
                    title: t('global', keyMetric.pinned ? 'unpin' : 'pin')
                  },
                  {
                    divider: true,
                    iconName: 'Edit' as const,
                    onClick: () => onEdit(keyMetric),
                    title: t('global', 'edit')
                  },
                  {
                    color: 'error',
                    iconName: 'Delete' as const,
                    onClick: () => onDelete(keyMetric),
                    title: t('key_metrics', 'delete_key_metric')
                  }
                ]}
                size="small"
              />
            )}
          </Box>
        </TableCell>
      ) : null}
    </>
  )
}

export default KeyMetricCells
